import React from "react";
import styles from "./index.module.scss";
import { formatCurrency, formatNumber, str2number } from "../../../libs/helpers";
import { LogisticsFeeResponseData } from "../../../apis/models/LogisticsFeeResponse";
import { Button } from "@mui/material";
import GeneralDialog from "../../core/GeneralDialog";
import DateCell from "../../core/DateCell";
import CloseIcon from "@mui/icons-material/Close";
import InputCell from "../../core/InputCell";
import DateWithoutTime from "../../../libs/dateWithoutTime";
import { InvoiceMasterData } from "../../../apis/models/InvoiceMasterRequest";
import { InvStatus } from "../../../libs/constants";
import LogisticsFeeDialog from "../../dialogs/invoiceDetails/LogisticsFeeDialog";

type InputField = {
  dateAdded: DateWithoutTime,
  productNumber: string,
  colors: string,
  inspectionUnitPrice: string,
  inspectionQuantity: string,
  shippingUnitPrice: string,
  shippingQuantity: string,
  memo: string,
}

type ErrorInfo = {
  dateAdded?: string;
  inspectionQuantity?: string;
  inspectionUnitPrice?: string;
  shippingQuantity?: string;
  shippingUnitPrice?: string;
}

export type LogisticFeeTabType = InputField & {
  error: ErrorInfo
}

const LogisticsFee: React.FC<LogisticsFeeProps> = (props: LogisticsFeeProps) => {
  const [openWarningDialog, setOpenWarningDialog] =React.useState<boolean>(false);
  const [openAddDialog, setOpenAddDialog] = React.useState<boolean>(false);

  const handleDateChange = (date: Date | null, rowIndex: number) => {
    const newItem = props.tabValues.map((fee, index) => {
      if (index === rowIndex) {
        fee.dateAdded = new DateWithoutTime(date);
      }
      fee.error.dateAdded = validateDateAdded(fee.dateAdded)
      return fee;
    })
    props.onChange(newItem);
  }

  const handleTextChange = (value: string | number, rowIndex: number, type: "product" | "colors" | "memo") => {
    const newItem = props.tabValues.map((fee, index) => {
      if (index === rowIndex) {
        if (type === "product") {
          fee.productNumber = String(value);
        } else if (type === "colors") {
          fee.colors = String(value);
        } else if (type === "memo") {
          fee.memo = String(value);
        }
      }
      return fee;
    })
    props.onChange(newItem);
  }

  const handleRequiredNumberChange = (value: string | number, rowIndex: number, type: "inspectPrice" | "inspectQuantity" | "shippingPrice" | "shippingQuantity") => {
    const newItem = props.tabValues.map((fee, index) => {
      if (index === rowIndex) {
        if (type === "inspectPrice") {
          fee.inspectionUnitPrice = String(value);
          fee.error.inspectionUnitPrice = validateNumber(fee.inspectionUnitPrice);
        } else if (type === "inspectQuantity") {
          fee.inspectionQuantity = String(value);
          fee.error.inspectionQuantity = validateNumber(fee.inspectionQuantity);
        } else if (type === "shippingPrice") {
          fee.shippingUnitPrice = String(value);
          fee.error.shippingUnitPrice = validateNumber(fee.shippingUnitPrice);
        } else if (type === "shippingQuantity") {
          fee.shippingQuantity = String(value);
          fee.error.shippingQuantity = validateNumber(fee.shippingQuantity);
        }
      }
      return fee;
    })
    props.onChange(newItem);
  }

  const onDelete = (rowIndex: number) => {
    props.onChange(props.tabValues.filter((_, index) => index !== rowIndex));
  }

  const handleSaveDialog = (items: LogisticsFeeResponseData[]) => {
    const addItems = items.map(item => {
      return {
        dateAdded: new DateWithoutTime(item.dateAdded),
        productNumber: item.productNumber,
        colors: item.colors,
        inspectionQuantity: String(item.inspectionQuantity),
        inspectionUnitPrice: String(item.inspectionUnitPrice),
        shippingQuantity: String(item.shippingQuantity),
        shippingUnitPrice: String(item.shippingUnitPrice),
        memo: item.memo,
        error: {}
      } as LogisticFeeTabType
    })
    props.onChange(props.tabValues.concat(addItems));
    setOpenAddDialog(false);
  }

  const isConfirmed = React.useMemo(() => {
    return props.targetInvoice?.status === InvStatus.CONFIRMED || props.targetInvoice?.status === InvStatus.EXPORT;
  }, [props.targetInvoice])

  const totalInfo = React.useMemo(() => {
    let inspectionPrice = 0;
    let inspectionQuantity = 0;
    let shippingPrice = 0;
    let shippingQuantity = 0;
    props.tabValues.forEach(rowItem => {
      inspectionPrice = inspectionPrice + str2number(rowItem.inspectionQuantity) * str2number(rowItem.inspectionUnitPrice);
      inspectionQuantity = inspectionQuantity + str2number(rowItem.inspectionQuantity);
      shippingPrice = shippingPrice + str2number(rowItem.shippingQuantity) * str2number(rowItem.shippingUnitPrice);
      shippingQuantity = shippingQuantity + str2number(rowItem.shippingQuantity);
    })
    return {inspectionPrice, inspectionQuantity, shippingPrice, shippingQuantity};
  }, [props.tabValues]);

  const validateDateAdded = (value: DateWithoutTime): string | undefined => {
    const dateString = value.getValue4Display()
    if (value.getValue4Display().length === 0) {
      return "入力必須です"
    } else if (value.isInvalid()) {
      return "年月日で入力してください"
    } else {
      const invoiceFrom = new DateWithoutTime(props.targetInvoice.from)
      const invoiceTo = new DateWithoutTime(props.targetInvoice.to)
      if (dateString < invoiceFrom.getValue4Display() || invoiceTo.getValue4Display() < dateString) {
        return "請求期間の範囲で入力してください"
      }
    }
    return undefined
  }

  const validateNumber = (value: string) => {
    if (value.length === 0) {
      return "入力必須です"
    } else if (isNaN(+value) || parseInt(value) < 1) {
      return "１以上を入力してください"
    }
    return undefined
  }

  const hasError = React.useMemo(() => {
    return props.tabValues.some(fee => {
      return (
        fee.error.dateAdded
        || fee.error.inspectionQuantity
        || fee.error.inspectionUnitPrice 
        || fee.error.shippingQuantity 
        || fee.error.shippingUnitPrice
      )
    })
  },[props.tabValues]);

  return (
    <>
      <div className={styles.container}>
        <div className={styles.content__header}>
          <div className={styles.content__header_statistics}>
            <div>
              合計検数金額：<span>{formatCurrency(totalInfo.inspectionPrice)}</span>
            </div>
            <div>
              合計出荷金額：<span>{formatCurrency(totalInfo.shippingPrice)}</span>
            </div>
            <div>
              合計検数：<span>{formatNumber(totalInfo.inspectionQuantity)}</span>
            </div>
            <div>
              合計出荷数：<span>{formatNumber(totalInfo.shippingQuantity)}</span>
            </div>
          </div>
          {isConfirmed ? (
            <div className={styles.download_section}>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setOpenWarningDialog(true)}
              >
                編集
              </Button>
            </div>
          ) : (
            <div className={styles.download_section}>
              <Button variant="outlined" color="primary" onClick={() => setOpenAddDialog(true)}>
                ＋項目追加
              </Button>
              <Button variant="outlined" color="primary" onClick={() => props.onClickUpload()}>
                物流費アップロード
              </Button>
            </div>
          )}
        </div>
        <div className={styles.table__container}>
          <table className={styles.table__wrapper}>
            <thead className={styles.table__head}>
              <tr className={styles.table__head_row}>
                <td colSpan={1}>日付</td>
                <td colSpan={1}>品番</td>
                <td colSpan={1}>色</td>
                <td colSpan={3}>検数</td>
                <td colSpan={3}>出荷</td>
                <td colSpan={1}>備考</td>
                {!isConfirmed ? <td colSpan={1}></td> : <td style={{display: "none"}}></td>}
              </tr>
              <tr className={styles.table__head_rowsub}>
                <td colSpan={1} style={{width: "142px"}}></td>
                <td colSpan={1}></td>
                <td colSpan={1}></td>
                <td colSpan={1} style={{width: "70px"}}>単価</td>
                <td colSpan={1} style={{width: "70px"}}>枚数</td>
                <td colSpan={1} style={{width: "70px"}}>金額</td>
                <td colSpan={1} style={{width: "70px"}}>単価</td>
                <td colSpan={1} style={{width: "70px"}}>枚数</td>
                <td colSpan={1} style={{width: "70px"}}>金額</td>
                <td colSpan={1}></td>
                {!isConfirmed ? <td colSpan={1}></td> : <td style={{display: "none"}}></td>}
              </tr>
            </thead>
            <tbody className={styles.table__body}>
              {props.tabValues.map((item, index) => (
                <tr key={index}>
                  <td colSpan={1} className={!isConfirmed ? `${styles.unconfirmed}` : ""}>
                    <DateCell
                      isConfirmed={isConfirmed}
                      onChangeDate={(val) => handleDateChange(val, index)} 
                      value={item.dateAdded.getValue4Display()}
                    />
                  </td>
                  <td colSpan={1} className={!isConfirmed ? `${styles.unconfirmed}` : ""}>
                    <InputCell
                      isConfirmed={isConfirmed}
                      onChangeText={(val) => handleTextChange(val, index, "product")}
                      type="text"
                      value={item.productNumber}
                      textAlignment={"left"}
                    />
                  </td>
                  <td colSpan={1} className={!isConfirmed ? `${styles.unconfirmed}` : ""}>
                    <InputCell
                        isConfirmed={isConfirmed}
                        onChangeText={(val) => handleTextChange(val, index, "colors")}
                        type="text"
                        value={item.colors}
                        textAlignment={"left"}
                    />
                  </td>
                  <td colSpan={1} className={!isConfirmed ? `${styles.unconfirmed}` : ""}>
                    <InputCell
                        isConfirmed={isConfirmed}
                        onChangeText={(val) => handleRequiredNumberChange(val, index, "inspectPrice")}
                        type="number"
                        value={item.inspectionUnitPrice}
                        textAlignment={"right"}
                    />
                  </td>
                  <td colSpan={1} className={!isConfirmed ? `${styles.unconfirmed}` : ""}>
                      <InputCell
                        isConfirmed={isConfirmed}
                        onChangeText={(val) => handleRequiredNumberChange(val, index, "inspectQuantity")}
                        type="number"
                        value={item.inspectionQuantity}
                        textAlignment={"right"}
                      />
                  </td>
                  <td colSpan={1} className={!isConfirmed ? `${styles.disabled}` : ""} style={{textAlign: "right"}}>
                    {formatNumber(
                      str2number(item.inspectionUnitPrice) * str2number(item.inspectionQuantity)
                    )}
                  </td>
                  <td colSpan={1} className={!isConfirmed ? `${styles.unconfirmed}` : ""}>
                    <InputCell
                        isConfirmed={isConfirmed}
                        onChangeText={(val) => handleRequiredNumberChange(val, index, "shippingPrice")}
                        type="number"
                        value={item.shippingUnitPrice}
                        textAlignment={"right"}
                      />
                  </td>
                  <td colSpan={1} className={!isConfirmed ? `${styles.unconfirmed}` : ""}>
                      <InputCell
                        isConfirmed={isConfirmed}
                        onChangeText={(val) => handleRequiredNumberChange(val, index, "shippingQuantity")}
                        type="number"
                        value={item.shippingQuantity}
                        textAlignment={"right"}
                      />
                  </td>
                  <td colSpan={1} className={!isConfirmed ? `${styles.disabled}` : ""} style={{textAlign: "right"}}>
                    {formatNumber(
                      str2number(item.shippingUnitPrice) * str2number(item.shippingQuantity)
                    )}
                  </td>
                  <td colSpan={1} className={!isConfirmed ? `${styles.unconfirmed}` : ""}>
                    <InputCell
                        isConfirmed={isConfirmed}
                        onChangeText={(val) => handleTextChange(val, index, "memo")}
                        type="text"
                        value={item.memo}
                        textAlignment={"left"}
                      />
                  </td>
                  {!isConfirmed ? (
                    <td>
                      <CloseIcon color="primary" className={styles.delete_icon} onClick={() => onDelete(index)}/>
                    </td>
                  ) : (
                    <></>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <GeneralDialog
        open={openWarningDialog}
        onCancel={() => setOpenWarningDialog(false)}
        cancelText="キャンセル"
        onSave={() => {
          props.onNotYetConfirmed();
          setOpenWarningDialog(false);
        }}
        saveText="確定を取り消す"
        title="請求確定取り消し"
        onClose={() => setOpenWarningDialog(false)}
      >
        <div>
          既に確定済の請求です。<br />
          内容を編集するには請求の確定取り消しが必要です。<br/><br/>
        </div>
        <div>請求の確定を取り消しますか？</div>
      </GeneralDialog>
      {!isConfirmed ? (
        <div className={styles.functions}>
          <Button 
            variant="outlined" 
            color="primary" 
            onClick={() => props.onReset()} 
            disabled={!props.isChanged}
          >
            編集内容を破棄
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => props.onSave()}
            disabled={hasError || !props.isChanged}
          >
            保存
          </Button>
        </div>
      ) : (
        <></>
      )}
    {
      openAddDialog ? (
        <LogisticsFeeDialog
          targetBrand={props.targetInvoice.brand}
          targetInvoice={props.targetInvoice}
          open={openAddDialog} 
          onClose={() => setOpenAddDialog(false)}
          onSave={(val) => handleSaveDialog(val)}
        />
      ) : (
        <></>
      )
    }
    </>
  );
};

export default LogisticsFee;

export type LogisticsFeeProps = {
  targetInvoice: InvoiceMasterData;
  tabValues: LogisticFeeTabType[];
  isChanged: boolean;
  onNotYetConfirmed: () => void;
  onReset: () => void;
  onSave: () => void;
  onChange: (val: LogisticFeeTabType[]) => void;
  onClickUpload: () => void;
};
